import { useState } from 'react';
import { daysAgo, isoDay } from '../lib/dates';
import { TextInput } from './ui';

export type RangePreset = 'today' | '7d' | '30d' | 'all' | 'custom';
type QuickPreset = Exclude<RangePreset, 'custom'>;

const LABELS: Record<QuickPreset, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  all: 'All dates',
};

/** The dates a quick button stands for; `all` is no limit at either end. */
export const rangeOf = (preset: QuickPreset): { from: string; to: string } => {
  if (preset === 'all') return { from: '', to: '' };
  if (preset === '7d') return { from: daysAgo(6), to: isoDay() };
  if (preset === '30d') return { from: daysAgo(29), to: isoDay() };
  return { from: isoDay(), to: isoDay() };
};

/**
 * A date range that starts on `initial` (today, unless a screen says otherwise)
 * and can be moved to a quick range or to any two dates.
 *
 * Typing a date makes the range `custom` rather than leaving a quick button lit
 * for dates it no longer stands for.
 */
export function useDateRange(initial: QuickPreset = 'today') {
  const [preset, setPreset] = useState<RangePreset>(initial);
  const [from, setFromDate] = useState(() => rangeOf(initial).from);
  const [to, setToDate] = useState(() => rangeOf(initial).to);

  return {
    preset,
    from,
    to,
    initial,
    /** Still on the screen's own starting range. */
    isInitial: preset === initial,
    choose: (next: QuickPreset) => {
      setPreset(next);
      setFromDate(rangeOf(next).from);
      setToDate(rangeOf(next).to);
    },
    // Moving one end past the other drags the other along, so the range can never
    // be backwards. The date boxes also limit each other, but that only stops a
    // click on the picker, not a typed or pasted date.
    setFrom: (value: string) => {
      setFromDate(value);
      if (value && to && value > to) setToDate(value);
      setPreset('custom');
    },
    setTo: (value: string) => {
      setToDate(value);
      if (value && from && value < from) setFromDate(value);
      setPreset('custom');
    },
  };
}

export type DateRange = ReturnType<typeof useDateRange>;

/**
 * Quick ranges and two date boxes, for a `.filter-bar`.
 *
 * The boxes limit each other (From cannot pass To) in the picker, and the range
 * itself keeps them in order for anything typed past that.
 */
export function DateRangeBar({
  range,
  presets = ['today', '7d', '30d', 'all'],
}: {
  range: DateRange;
  presets?: QuickPreset[];
}) {
  return (
    <>
      <div className="seg" role="group" aria-label="Date range">
        {presets.map((id) => (
          <button
            key={id}
            type="button"
            className={id === range.preset ? 'active' : ''}
            onClick={() => range.choose(id)}
          >
            {LABELS[id]}
          </button>
        ))}
      </div>
      <TextInput
        label="From"
        type="date"
        value={range.from}
        max={range.to || undefined}
        onChange={(e) => range.setFrom(e.target.value)}
      />
      <TextInput
        label="To"
        type="date"
        value={range.to}
        min={range.from || undefined}
        onChange={(e) => range.setTo(e.target.value)}
      />
    </>
  );
}
